import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React from 'react';
import TagIcon from 'react-feather/dist/icons/tag';
import { formatPropertyBasedOnNameValue } from '@jupiterone/jupiter-web-toolkit/src/components/util/formatter';
import useEntityDrawer from '@jupiterone/jupiter-web-toolkit/src/components/drawers/EntityDrawer/useEntityDrawerHook';
import { J1QLListData } from '@jupiterone/jupiter-web-toolkit/types/J1QL';
import { isAggregateColumn } from './util/isAggregateColumn';

const useStyles = makeStyles((theme: any) =>
  createStyles({
    table: {
      '&&': {
        marginBottom: 0,
      },
    },
    active: {
      border: `2px solid ${theme.palette.primary.main}`,
    },
    tableHeader: {
      background: theme.palette.navy[50],
      textTransform: 'none',
      position: 'sticky',
      top: 0,
      zIndex: 2,
    },
    headCellIcon: {
      color: theme.palette.coolGrey[300],
      marginRight: '0.25em',
    },
    textCell: {
      overflow: 'hidden',
      display: '-webkit-box',
      '-webkit-line-clamp': 7,
      '-webkit-box-orient': 'vertical',
    },
  })
);

interface Props {
  columns: any[];
  sortedColumn: string;
  sortedData: any[];
  sortAsc: boolean;
  page: number;
  rowsPerPage: number;
  onSortClick: (direction: string) => void;
  handleCellClick: (data: any) => void;
  selectedRowID: string | null | undefined;
  useRowDataForEntityDrawer?: boolean;
  rawData: NonNullable<J1QLListData['data']>;
}

export const QueryResultTableBody = ({
  columns,
  sortedColumn,
  sortAsc,
  sortedData,
  page,
  rowsPerPage,
  selectedRowID,
  useRowDataForEntityDrawer,
  onSortClick,
  handleCellClick,
  rawData,
}: Props) => {
  const classes = useStyles();
  const { setVertex, setVertexId } = useEntityDrawer();
  function getClass(classValue: string | string[]): string {
    return Array.isArray(classValue) ? classValue.join(',') : classValue;
  }
  function formatEntity(entity: any) {
    return `{Entity:${entity._id}:${getClass(entity._class)}}`;
  }

  function formatRelationship(relationship: any) {
    return `{Relationship:${relationship._id}}:${getClass(
      relationship._class
    )}`;
  }
  function formatGraphObject(graphObject: any) {
    return graphObject.entity
      ? formatEntity(graphObject.entity)
      : formatRelationship(graphObject.relationship);
  }

  const formatCellValue = (value: any, column: string) => {
    if (value === undefined || value === null) {
      return '';
    } else {
      if (typeof value === 'object') {
        if (value.entity || value.relationship) {
          return formatGraphObject(value);
        } else {
          if (Array.isArray(value)) {
            return value.join(', ');
          } else {
            return JSON.stringify(value);
          }
        }
      } else {
        return formatPropertyBasedOnNameValue(column, value);
      }
    }
  };
  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow>
          {columns.map((column: any, index: number) => {
            const columnName =
              column.toLowerCase() === 'tags'
                ? column
                : column.replace(/^tag./, '');
            return (
              <TableCell
                classes={{ root: classes.tableHeader }}
                key={`${columnName}${index}`}
              >
                {isAggregateColumn(columnName) ? (
                  columnName
                ) : (
                  <TableSortLabel
                    active={column === sortedColumn}
                    direction={sortAsc ? 'asc' : 'desc'}
                    onClick={() => onSortClick(column)}
                  >
                    {column.match(/^tag./) &&
                      column.toLowerCase() !== 'tags' && (
                        <TagIcon className={classes.headCellIcon} size={16} />
                      )}
                    {columnName}
                  </TableSortLabel>
                )}
              </TableCell>
            );
          })}
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedData
          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
          .map((data: any, index: number) => (
            <TableRow
              key={index}
              className={clsx(
                data._vertexID === selectedRowID && classes.active
              )}
            >
              {columns.map((column) => {
                const value = data[column];
                return (
                  <TableCell
                    key={`${column}:${value}`}
                    onClick={() => {
                      if (useRowDataForEntityDrawer)
                        setVertex(
                          rawData.find((d: any) => d?.id === data._vertexID)
                        );
                      else setVertexId(data._vertexID);
                      handleCellClick(data);
                    }}
                  >
                    <div className={classes.textCell}>
                      {formatCellValue(value, column)}
                    </div>
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
};

export default QueryResultTableBody;
